import { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import ResearchFileFormDialog from "@/components/dialogs/ResearchFileFormDialog";
import api, { fileUrl, downloadUrl } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Search, FileText, Eye, Download, Pencil, Trash2, Archive } from "lucide-react";

export default function ResearchArchive() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [files, setFiles] = useState([]);
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editingFile, setEditingFile] = useState(null);

  const load = (q) => api.get("/research", { params: q ? { search: q } : {} }).then((r) => setFiles(r.data)).catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    load(e.target.value);
  };

  const deleteFile = async (id) => {
    try {
      await api.delete(`/research/${id}`);
      toast.success("Research file removed");
      load(search);
    } catch (e) {
      toast.error("Could not remove file");
    }
  };

  const openAdd = () => {
    setEditingFile(null);
    setFormOpen(true);
  };

  const openEdit = (f) => {
    setEditingFile(f);
    setFormOpen(true);
  };

  return (
    <DashboardLayout title="Research Archive">
      <div className="flex items-center justify-between mb-6 gap-3">
        <div className="relative w-80">
          <Search className="h-4 w-4 text-[#4B5563] absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="research-search-input"
            placeholder="Search by title, employee, project, year..."
            value={search}
            onChange={handleSearch}
            className="pl-9"
          />
        </div>
        {isAdmin && (
          <Button data-testid="add-research-button" onClick={openAdd} className="bg-[#76C121] hover:bg-[#5C9819] text-white">
            <Plus className="h-4 w-4 mr-1.5" /> Add Research File
          </Button>
        )}
      </div>

      {files.length === 0 ? (
        <div className="bg-white border border-[#E2E8F0] rounded-lg py-16 text-center text-[#4B5563]" data-testid="research-empty-state">
          <Archive className="h-8 w-8 mx-auto mb-2 text-[#76C121]" />
          No research files archived yet.
        </div>
      ) : (
        <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="research-archive-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-[#4B5563] border-b border-[#E2E8F0] bg-[#F8F9FA]">
                <th className="px-5 py-3 font-medium">Title</th>
                <th className="px-5 py-3 font-medium">Employee</th>
                <th className="px-5 py-3 font-medium">Project / Client</th>
                <th className="px-5 py-3 font-medium">Category</th>
                <th className="px-5 py-3 font-medium">Year</th>
                <th className="px-5 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {files.map((f) => (
                <tr key={f.id} data-testid={`research-row-${f.id}`} className="border-b border-[#E2E8F0] last:border-b-0 hover:bg-[#F8F9FA] transition-colors duration-200">
                  <td className="px-5 py-3 font-medium text-[#1C1C1E] flex items-center gap-2 max-w-[240px]">
                    <FileText className="h-4 w-4 text-[#00823B] shrink-0" />
                    <span className="truncate">{f.title}</span>
                  </td>
                  <td className="px-5 py-3 text-[#4B5563]">{f.employee_name}</td>
                  <td className="px-5 py-3 text-[#4B5563]">{f.project_or_client || "—"}</td>
                  <td className="px-5 py-3 text-[#4B5563]">{f.category || "—"}</td>
                  <td className="px-5 py-3 text-[#4B5563]">{f.year || "—"}</td>
                  <td className="px-5 py-3 flex items-center gap-3 justify-end">
                    <a href={fileUrl(f.file_path)} target="_blank" rel="noreferrer" data-testid={`research-view-${f.id}`} title="View">
                      <Eye className="h-4 w-4 text-[#4B5563]" />
                    </a>
                    <a href={downloadUrl(f.file_path, f.title)} data-testid={`research-download-${f.id}`} title="Download">
                      <Download className="h-4 w-4 text-[#00823B]" />
                    </a>
                    {isAdmin && (
                      <>
                        <button onClick={() => openEdit(f)} data-testid={`research-edit-${f.id}`} className="text-[#4B5563] hover:text-[#1C1C1E]" title="Edit">
                          <Pencil className="h-4 w-4" />
                        </button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button data-testid={`research-delete-${f.id}`} className="text-[#4B5563] hover:text-red-600" title="Delete">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete "{f.title}"?</AlertDialogTitle>
                              <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => deleteFile(f.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ResearchFileFormDialog open={formOpen} onOpenChange={setFormOpen} file={editingFile} onCreated={() => load(search)} />
    </DashboardLayout>
  );
}
